import React, { useState, useEffect } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody, Box,
  IconButton, Button, Dialog, DialogTitle, DialogContent
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Current } from "../../models/signalUsers/current.ts";
import { Signal } from "../../models/signal.ts";
import EditCurrentForm from "./EditCurrentForm.tsx";
import { getSignalLabelFromList } from "../../utils/signalUtils.ts";
import { v4 as uuidv4 } from 'uuid';
import { useInvalidRowStyle } from "../../utils/getInvalidRowStyle.ts";

interface Props {
  data: Current[];
  onChange: (items: Current[]) => void;
  signalOptions: Signal[];
}

const emptyCurrent: Current = {
  high_signal: { name: "", net: "", id: "" },
  low_signal: { name: "", net: "", id: "" },
  max: 0,
  shunt: 0,
  power: 0,
  id: "",
  index: 0
};

const CurrentTable: React.FC<Props> = ({ data, onChange, signalOptions }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogConentIsValid, setDialogConentIsValid] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editCurrent, setEditCurrent] = useState<Current>(emptyCurrent);
  const getRowStyle = useInvalidRowStyle();

  const reindex = (items: Current[]): Current[] =>
    items
      .slice()
      .sort((a, b) => a.index - b.index)
      .map((item, i) => ({ ...item, index: i }));

  useEffect(() => {
    const validIds = new Set(signalOptions.map(s => s.id));
    const cleaned = data.filter(item =>
      validIds.has(item.high_signal.id) && validIds.has(item.low_signal.id)
    );
    if (cleaned.length !== data.length) {
      onChange(cleaned);
    }
  }, [signalOptions, data, onChange]);

  const handleSave = () => {
    const updated = [...data];
    if (editIndex !== null) {
      updated[editIndex] = editCurrent;
    } else {
      updated.push(editCurrent);
    }
    onChange(updated);
    setDialogConentIsValid(false);
    setOpenDialog(false);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setDialogConentIsValid(false);
    setEditIndex(null);
    setEditCurrent(emptyCurrent);
  };

  const handleAdd = () => {
    setEditIndex(null); 
    const edit = emptyCurrent;
    edit.id = uuidv4();
    edit.index = data.length;
    setEditCurrent(edit);
    setOpenDialog(true); 
  };

  return (
    <>
      {data.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Signal +</TableCell>
              <TableCell>Signal -</TableCell>
              <TableCell>Max. Strom (A)</TableCell>
              <TableCell>Shunt-Widerstand (mΩ)</TableCell>
              <TableCell>Verlustleistung (mW)</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const duplicateIndices = new Set<number>();
              const seen = new Map<string, number>();
              data.forEach((item, index) => {
                const key = [item.high_signal.id, item.low_signal.id].sort().join("|");
                if (seen.has(key)) {
                  duplicateIndices.add(index);
                  duplicateIndices.add(seen.get(key)!);
                } else {
                  seen.set(key, index);
                }
              });
              return (
                <TableRow 
                  key={item.index}
                  sx={getRowStyle(duplicateIndices.has(index))}
                >
                  <TableCell>{getSignalLabelFromList(item.high_signal.id, signalOptions)}</TableCell>
                  <TableCell>{getSignalLabelFromList(item.low_signal.id, signalOptions)}</TableCell>
                  <TableCell>{item.max}</TableCell>
                  <TableCell>{(item.shunt*1000).toFixed(1)}</TableCell>
                  <TableCell>{(item.power*1000).toFixed(0)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => { setEditCurrent(item); setEditIndex(index); setOpenDialog(true); }}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => onChange(reindex(data.filter((_, i) => i !== index)))}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Box mt={2} display="flex" gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => { handleAdd() }}
          disabled={signalOptions.length === 0}
        >
          Hinzufügen
        </Button>
      </Box>
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Strommessung bearbeiten</DialogTitle>
        <DialogContent>
          <EditCurrentForm data={editCurrent} onChange={setEditCurrent} setIsValid={setDialogConentIsValid} signalOptions={signalOptions} />
          <Box mt={2} display="flex" gap={2}>
            <Button variant="contained" onClick={handleSave} disabled={!dialogConentIsValid}>Speichern</Button>
            <Button variant="outlined" onClick={handleClose}>Abbrechen</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CurrentTable;
